import { useEffect, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { usePlatform, prepareAsyncOpenTab } from '@shared/platform'
import { SECRET_BOT_URL } from '@shared/config/routes'
import { ENV } from '@shared/config/env'
import { useRetrySubscriptionIssueMutation } from '@features/subscription'
import { LinkTelegramWaitModal } from '@features/link-telegram/ui/LinkTelegramWaitModal'
import { generateLinkTelegram } from '@features/link-telegram/api/linkTelegramApi'
import type { SubscriptionIssueStatus } from '@entities/user'
import styles from './SubscriptionSection.module.css'

interface SubscriptionIssueBannerProps {
  status: SubscriptionIssueStatus | null | undefined
}

/** Бэк принимает повторную выдачу не чаще раза в минуту. */
const RETRY_COOLDOWN_SEC = 60

/** Текст под каждую причину недоставки. */
const ISSUE_TEXT: Record<SubscriptionIssueStatus, string> = {
  not_linked:
    'Подписка оплачена, но доступ к материалам ещё не выдан: к аккаунту не привязан Telegram.',
  bot_not_activated:
    'Подписка оплачена, но доступ к материалам ещё не выдан: нужно запустить нашего Telegram-бота.',
  not_configured:
    'Подписка оплачена, но доступ к материалам не выдался из-за ошибки на нашей стороне. Напишите в поддержку — поможем.',
  internal_error:
    'Подписка оплачена, но доступ к материалам не выдался из-за ошибки на нашей стороне. Напишите в поддержку — поможем.',
}

/** Подпись действия под каждую причину. */
const ACTION_LABEL: Record<SubscriptionIssueStatus, string> = {
  not_linked: 'Привязать Telegram',
  bot_not_activated: 'Запустить бота',
  not_configured: 'Написать в поддержку',
  internal_error: 'Написать в поддержку',
}

/**
 * Предупреждение о том, что подписка есть, а доступ не выдан.
 * Показывается при `issue_canceled`; действие зависит от `issue_status`.
 */
export const SubscriptionIssueBanner = ({
  status,
}: SubscriptionIssueBannerProps) => {
  const platform = usePlatform()
  const retry = useRetrySubscriptionIssueMutation()
  const [waitTgOpen, setWaitTgOpen] = useState(false)
  const [linkError, setLinkError] = useState<string | null>(null)
  // Бэк принимает повтор не чаще раза в минуту — не даём словить отказ.
  const [cooldown, setCooldown] = useState(0)

  useEffect(() => {
    if (cooldown <= 0) return
    const timer = window.setTimeout(() => setCooldown(cooldown - 1), 1000)
    return () => clearTimeout(timer)
  }, [cooldown])

  const handleRetry = () => {
    if (cooldown > 0 || retry.isLoading) return
    setCooldown(RETRY_COOLDOWN_SEC)
    retry.mutate()
  }

  // Статус может не приехать — тогда показываем нейтральный текст без действия.
  // В мини-аппе «не привязан Telegram» звучит абсурдно (человек уже в нём),
  // поэтому там объясняем через запуск бота.
  const text = !status
    ? 'Подписка оплачена, но доступ к материалам ещё не выдан.'
    : status === 'not_linked' && !ENV.IS_WEB
      ? 'Подписка оплачена, но доступ к материалам ещё не выдан: нужно запустить нашего Telegram-бота.'
      : ISSUE_TEXT[status]

  // Ручка генерации ссылки доступна только web-авторизации: из мини-аппа она
  // отвечает 400 (у такого пользователя Telegram уже есть). Поэтому привязку
  // предлагаем только в вебе, а в Telegram ведём в бота — связка создаётся
  // при его запуске.
  const canLinkTelegram = status === 'not_linked' && ENV.IS_WEB

  const actionLabel = !status
    ? null
    : status === 'not_linked'
      ? canLinkTelegram
        ? 'Привязать Telegram'
        : 'Запустить бота'
      : ACTION_LABEL[status]

  const openBot = () => platform.openTelegramLink(SECRET_BOT_URL)

  const handleLinkTelegram = async () => {
    setLinkError(null)
    // Окно открываем синхронно — иначе браузер заблокирует popup после await.
    const tab = prepareAsyncOpenTab(platform)
    try {
      const { link } = await generateLinkTelegram()
      tab.complete(link)
      setWaitTgOpen(true)
    } catch {
      tab.abort()
      setLinkError('Не удалось сгенерировать ссылку. Попробуйте позже.')
    }
  }

  const handleAction = () => {
    if (canLinkTelegram) {
      void handleLinkTelegram()
      return
    }
    openBot()
  }

  // Отказ приходит со статусом в 200-м ответе, а не ошибкой запроса.
  const retryStatus = retry.data?.status
  const retryHint = retry.isLoading
    ? 'Проверяем…'
    : retryStatus === 'queued'
      ? 'Запросили выдачу — доступ появится в течение пары минут.'
      : retryStatus === 'not_linked'
        ? 'Telegram всё ещё не привязан.'
        : retryStatus
          ? 'Пока не получилось — попробуйте чуть позже.'
          : retry.isError
            ? 'Не удалось отправить запрос. Попробуйте позже.'
            : null

  return (
    <>
      <div className={styles.graceBanner}>
        <AlertTriangle size={18} className={styles.graceBannerIcon} aria-hidden />
        <div className={styles.issueBannerBody}>
          <p className={styles.graceBannerText}>{text}</p>

          {status && (
            <button
              type="button"
              className={styles.issueActionButton}
              onClick={handleAction}
            >
              {actionLabel}
            </button>
          )}

          {/* Ручная перепроверка: доступ обычно выдаётся сам, но если человек
              уже всё сделал — даём не ждать. Текстом, чтобы не спорить с
              основным действием за внимание. */}
          <button
            type="button"
            className={styles.issueRetryLink}
            onClick={handleRetry}
            disabled={retry.isLoading || cooldown > 0}
          >
            {cooldown > 0
              ? `Проверить снова можно через ${cooldown} c`
              : 'Я уже всё сделал — проверить снова'}
          </button>

          {retryHint && <p className={styles.issueHint}>{retryHint}</p>}
          {linkError && <p className={styles.issueHint}>{linkError}</p>}
        </div>
      </div>

      {waitTgOpen && (
        <LinkTelegramWaitModal onClose={() => setWaitTgOpen(false)} />
      )}
    </>
  )
}
